import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, any> | null;
  read: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Subscribe to realtime updates
  useEffect(() => {
    if (!user) return;

    const listKey = ['notifications', user.id];
    const unreadKey = ['notifications', 'unread-count', user.id];

    // Per-mount unique channel name. Multiple consumers of this hook can mount
    // simultaneously; reusing the same channel name returns the already-subscribed
    // channel and `.on()` after `.subscribe()` throws. Cleanup removes the channel.
    const channel = supabase
      .channel(`notifications:${user.id}:${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // M6: targeted invalidation — invalidate ONLY the affected keys
          // instead of the `['notifications']` prefix (which double-invalidates
          // every list query + unread-count). For UPDATE (read flag flips) we
          // can also patch the unread count locally without a refetch.
          if (payload.eventType === 'UPDATE') {
            const oldRead = (payload.old as any)?.read;
            const newRead = (payload.new as any)?.read;
            if (oldRead === false && newRead === true) {
              queryClient.setQueryData<number>(unreadKey, (prev) =>
                Math.max(0, (prev ?? 0) - 1)
              );
            } else if (oldRead === true && newRead === false) {
              queryClient.setQueryData<number>(unreadKey, (prev) => (prev ?? 0) + 1);
            }
            queryClient.invalidateQueries({ queryKey: listKey });
          } else {
            queryClient.invalidateQueries({ queryKey: listKey });
            queryClient.invalidateQueries({ queryKey: unreadKey });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!user,
    staleTime: 30_000, // realtime channel invalidates on insert; 30s buffer prevents focus refetch storms
  });
}

export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notifications', 'unread-count', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (!user) return;
      // M6: targeted invalidation per key (not the broader `['notifications']` prefix).
      queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count', user.id] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user) return;
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      if (!user) return;
      queryClient.setQueryData<number>(['notifications', 'unread-count', user.id], 0);
      queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (!user) return;
      queryClient.setQueryData<number>(['notifications', 'unread-count', user.id], 0);
      queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
    },
  });
}
